// ReConnect's pages in the five steps (docs/specs/MODULE_NAVIGATION.md § Connect and ReConnect). The ReConnect agent
// owns this file. Screens are loaded with screen() (see ./screen.ts), never imported directly.
import { fiveSteps, type SteppedModule } from "./types";
import { screen } from "./screen";

const keepWarm = () => import("../screens/reconnect/KeepWarm");
const spoken = () => import("../screens/reconnect/SpokenBefore");
const updates = () => import("../screens/reconnect/WhatsAppUpdates");
const rules = () => import("../screens/reconnect/Rules");
const accounts = () => import("../screens/reconnect/Accounts");
const perf = () => import("../screens/reconnect/Performance");

const HistoryAccountPage = screen(accounts, "HistoryAccountPage");
const WhatsAppAccountPage = screen(accounts, "WhatsAppAccountPage");
const KeepWarmRulesPage = screen(rules, "KeepWarmRulesPage");
const OfferRulesPage = screen(rules, "OfferRulesPage");
const HowUpdatesGoPage = screen(updates, "HowUpdatesGoPage");
const ReconnectApprovalsPage = screen(rules, "ReconnectApprovalsPage");
const NotReadyPage = screen(keepWarm, "NotReadyPage");
const LostDealsPage = screen(keepWarm, "LostDealsPage");
const SpokenBeforePage = screen(spoken, "SpokenBeforePage");
const MovedPage = screen(() => import("../screens/reconnect/MovedCards"), "MovedPage");
const WaitingPage = screen(() => import("../screens/reconnect/Waiting"), "WaitingPage");
const PlanFollowUpsPage = screen(spoken, "PlanFollowUpsPage");
const WhatsAppUpdatesPage = screen(updates, "WhatsAppUpdatesPage");
const ResultsPage = screen(perf, "ResultsPage");
const HealthPage = screen(perf, "HealthPage");
// L-09 (item 85): bulk emails to people she already knows moved here from Find.
const BulkEmailsPage = screen(() => import("../screens/Campaigns"), "BulkEmailsPage");

export const reconnect: SteppedModule = {
  key: "reconnect",
  label: "ReConnect",
  layout: "steps",
  paused: "Keeps past customers and contacts warm, and brings them back when there is a reason to talk.",
  tabs: fiveSteps({
    accounts: [
      { slug: "history", label: "Your history", step: "0.40", render: () => <HistoryAccountPage /> },
      { slug: "whatsapp", label: "WhatsApp", step: "0.30", render: () => <WhatsAppAccountPage /> },
    ],
    rules: [
      { slug: "keep-warm", label: "Keep-warm rules", step: "10.11", render: () => <KeepWarmRulesPage /> },
      { slug: "offers", label: "Offers per loss reason", step: "10.10", render: () => <OfferRulesPage /> },
      { slug: "whatsapp-updates", label: "How WhatsApp updates go out", step: "10.13", render: () => <HowUpdatesGoPage /> },
      {
        slug: "signals",
        label: "Job moves and things worth sharing",
        step: "10.16",
        comingSoon: "Choose which signals Ogma acts on for people you know, such as a job move, a new contract or news about their firm, and when it shares something useful with them.",
      },
      { slug: "approvals", label: "Approvals", step: "E13", render: () => <ReconnectApprovalsPage /> },
    ],
    research: [
      { slug: "not-ready", label: "Not ready yet", step: "10.R", render: () => <NotReadyPage /> },
      { slug: "lost-deals", label: "Deals lost", step: "10.10", render: () => <LostDealsPage /> },
      { slug: "spoken-before", label: "Spoken to before", step: "10.15", render: () => <SpokenBeforePage /> },
      { slug: "moved", label: "They've moved", step: "10.16", render: () => <MovedPage /> },
    ],
    actions: [
      { slug: "waiting", label: "Waiting for you", step: "10.11", render: () => <WaitingPage /> },
      { slug: "follow-ups", label: "Plan follow-ups", step: "10.14", render: () => <PlanFollowUpsPage /> },
      { slug: "whatsapp", label: "WhatsApp updates", step: "10.13", render: () => <WhatsAppUpdatesPage /> },
      { slug: "bulk-emails", label: "Bulk emails", step: "4.11", render: () => <BulkEmailsPage /> },
      {
        slug: "offers",
        label: "Offers made",
        step: "10.10",
        comingSoon: "Every offer Ogma has made to a lost deal, what it was and whether they took it, so you can see each one before and after it goes.",
      },
    ],
    performance: [
      { slug: "results", label: "Results", step: "10.8", render: () => <ResultsPage /> },
      { slug: "health", label: "Health", step: "10.R", render: () => <HealthPage /> },
    ],
  }),
  taskHomes: {
    reconnect_return: ["/reconnect/actions/waiting"],
    // 10.0 a buyer's tender is coming up: get back in touch first (no other home until now).
    tender_reconnect: ["/reconnect/actions/waiting"],
    approve_whatsapp_update: ["/reconnect/actions/waiting", "/reconnect/actions/whatsapp"],
  },
};
